import { z } from "zod";
/**
 * Per-wedding "scratchpad" that the AI planner reads at session start and
 * appends to via the `remember_note` tool. Lets the agent carry durable
 * couple-level context across chat sessions (preferences they stated,
 * decisions made, follow-ups owed) without resorting to a vector store.
 *
 * Stored at `/weddings/{weddingId}/plannerMemory/main` (singleton).
 */
export const PLANNER_MEMORY_BUCKETS = ["preferences", "decisions", "followUps", "facts"];
export const PlannerMemoryBucketSchema = z.enum(PLANNER_MEMORY_BUCKETS);
/** Per-bucket cap. Keeps the system-prompt overhead bounded (~25 × 240 chars). */
export const PLANNER_MEMORY_BUCKET_CAP = 25;
export const PLANNER_MEMORY_NOTE_MAX_LEN = 240;
export const PlannerMemoryNoteSchema = z.object({
    id: z.string().min(1),
    text: z.string().min(1).max(PLANNER_MEMORY_NOTE_MAX_LEN),
    createdAt: z.string(),
    source: z.enum(["user", "planner"]),
    confidence: z.enum(["high", "low"]).optional(),
});
export const PlannerMemorySchema = z.object({
    preferences: z.array(PlannerMemoryNoteSchema).default([]),
    decisions: z.array(PlannerMemoryNoteSchema).default([]),
    followUps: z.array(PlannerMemoryNoteSchema).default([]),
    facts: z.array(PlannerMemoryNoteSchema).default([]),
    updatedAt: z.string().optional(),
    version: z.number().int().nonnegative().default(0),
});
export function emptyPlannerMemory() {
    return {
        preferences: [],
        decisions: [],
        followUps: [],
        facts: [],
        version: 0,
    };
}
/**
 * Drop the oldest low-confidence notes first, then oldest notes overall,
 * until the bucket fits the cap. Pure helper — easy to unit-test.
 */
export function pruneBucket(notes) {
    if (notes.length <= PLANNER_MEMORY_BUCKET_CAP)
        return notes;
    const withIndex = notes.map((n, i) => ({ n, i }));
    withIndex.sort((a, b) => {
        const aLow = a.n.confidence === "low" ? 1 : 0;
        const bLow = b.n.confidence === "low" ? 1 : 0;
        if (aLow !== bLow)
            return bLow - aLow;
        return Date.parse(a.n.createdAt) - Date.parse(b.n.createdAt);
    });
    const toEvict = new Set(withIndex.slice(0, notes.length - PLANNER_MEMORY_BUCKET_CAP).map((x) => x.i));
    return notes.filter((_, i) => !toEvict.has(i));
}
